import { useSyncExternalStore } from 'react'
import Echo from 'laravel-echo'
import Pusher from 'pusher-js'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4545/api'

/**
 * Laravel Reverb (pusher-протокол). Ключ обязан совпадать с REVERB_APP_KEY
 * бэкенда (v3.api.easya/.env), хост/порт — с REVERB_PUBLIC_PORT docker-compose.
 * Прод: VITE_REVERB_WS_HOST=api.easya.ru + VITE_REVERB_FORCE_TLS=true
 * (nginx проксирует /app с upgrade на контейнер reverb).
 */
const APP_KEY = import.meta.env.VITE_REVERB_APP_KEY || 'easya-key'
const WS_HOST = import.meta.env.VITE_REVERB_WS_HOST || 'localhost'
const WS_PORT = Number(import.meta.env.VITE_REVERB_WS_PORT || 6002)
const FORCE_TLS = String(import.meta.env.VITE_REVERB_FORCE_TLS || 'false') === 'true'

const ECHO_DEBUG = String(import.meta.env.VITE_ECHO_DEBUG || 'false') === 'true'

const getAuthToken = () => localStorage.getItem('auth_token')

/** Laravel: маршруты broadcasting в routes/api.php → /api/broadcasting/auth (JWT, без CSRF). */
const getAuthEndpoint = () => {
  const base = API_URL.replace(/\/api\/?$/, '')
  return `${base}/api/broadcasting/auth`
}

let echoInstance: Echo<'reverb'> | null = null

/**
 * Эпоха echo-клиента.
 *
 * Меняется только когда синглтон реально пересоздан (вход, выход,
 * impersonation) — то есть когда старый сокет отключён и все подписки на нём
 * мертвы. Хуки в `src/realtime/**` держали Echo в замыкании эффекта с
 * зависимостями вида `[lessonId]`: после пересоздания клиента эффект не
 * перезапускался, компонент оставался «подписан» на отключённый сокет и молча
 * переставал получать события — без единой ошибки в интерфейсе. Эпоха делает
 * пересоздание видимым для React: `useEchoClient()` возвращает новый инстанс,
 * эффекты с ним в зависимостях перезапускаются и переподписываются.
 */
let echoEpoch = 0
const epochListeners = new Set<() => void>()

function subscribeEchoEpoch(listener: () => void): () => void {
  epochListeners.add(listener)
  return () => {
    epochListeners.delete(listener)
  }
}

function getEchoEpoch(): number {
  return echoEpoch
}

function echoLog(...args: unknown[]) {
  if (ECHO_DEBUG || import.meta.env.DEV) {
    console.info('[Echo]', ...args)
  }
}

export function getEchoClient() {
  if (echoInstance) return echoInstance

  const token = getAuthToken()

  // laravel-echo для broadcaster="reverb" требует Pusher в window (или options.Pusher).
  ;(window as unknown as { Pusher?: typeof Pusher }).Pusher = Pusher

  echoInstance = new Echo({
    broadcaster: 'reverb',
    key: APP_KEY,
    wsHost: WS_HOST,
    wsPort: WS_PORT,
    wssPort: WS_PORT,
    forceTLS: FORCE_TLS,
    enabledTransports: ['ws', 'wss'],
    authEndpoint: getAuthEndpoint(),
    /**
     * Bearer подставляется на КАЖДЫЙ запрос авторизации канала, а не один раз
     * при создании клиента: pusher-js вызывает headersProvider() в момент
     * POST /broadcasting/auth. Со статичными `auth.headers` тихое обновление
     * JWT (по таймеру за 5 минут до истечения и на любом 401) требовало бы
     * пересоздания Echo, а пересоздание рвёт сокет и все живые подписки —
     * ученик, открывший урок заранее, переставал получать старт урока.
     * Сам WebSocket к Reverb авторизуется app-ключом и JWT не использует,
     * поэтому смена токена не требует переподключения вообще.
     */
    channelAuthorization: {
      transport: 'ajax',
      endpoint: getAuthEndpoint(),
      headersProvider: () => {
        const currentToken = getAuthToken()
        return currentToken ? { Authorization: `Bearer ${currentToken}` } : {}
      },
    },
    auth: {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
  })

  const pusher = getPusherUnsafe(echoInstance)
  if (pusher) {
    echoLog('Reverb host:', `${FORCE_TLS ? 'wss' : 'ws'}://${WS_HOST}:${WS_PORT}`)
    pusher.connection.bind('connected', () =>
      echoLog('Reverb connected', pusher.connection.socket_id),
    )
    pusher.connection.bind('disconnected', () => echoLog('Reverb disconnected'))
    pusher.connection.bind('error', (err: unknown) => echoLog('Reverb error', err))
    pusher.connection.bind('state_change', (states: { previous: string; current: string }) =>
      echoLog('Reverb state', states.previous, '->', states.current),
    )
  }

  return echoInstance!
}

/**
 * Текущий Echo для использования в эффектах.
 *
 * Возвращаемый инстанс обязан попадать в зависимости эффекта: тогда после
 * пересоздания клиента React перезапустит эффект и подписка восстановится.
 * Вне компонентов (см. `webrtc/callSignaling.ts`) берут `getEchoClient()`.
 */
export function useEchoClient(): Echo<'reverb'> {
  useSyncExternalStore(subscribeEchoEpoch, getEchoEpoch, getEchoEpoch)

  return getEchoClient()
}

/** Низкоуровневый pusher-клиент (для отладки в консоли). */
function getPusherUnsafe(echo: Echo<'reverb'>): Pusher | null {
  const connector = echo.connector as unknown as { pusher?: Pusher }
  return connector.pusher ?? null
}

export function getEchoSocket(): Pusher | null {
  if (!echoInstance) return null
  return getPusherUnsafe(echoInstance)
}

/**
 * Сбросить Echo при смене пользователя (вход, выход, impersonation).
 *
 * Обновление токена того же пользователя сюда не относится — заголовок
 * авторизации канала берётся из localStorage на каждый запрос (см.
 * headersProvider выше), рвать ради этого сокет незачем.
 */
export function resetEchoClient() {
  if (!echoInstance) return
  try {
    echoInstance.disconnect()
  } catch {
    /* ignore */
  }
  echoInstance = null

  // Инкремент строго после обнуления: слушатели перерисуются и запросят
  // клиента заново — уже нового.
  echoEpoch += 1
  for (const listener of epochListeners) {
    listener()
  }
}

export function disconnectEchoClient() {
  resetEchoClient()
}
